import { Trash2 } from "lucide-react";
import {
  Form,
  maxValue,
  minValue,
  required,
  useCreate,
  useDelete,
  useNotify,
  useTranslate,
  useUpdate,
} from "ra-core";
import { useMemo } from "react";
import type { FieldValues } from "react-hook-form";

import { BooleanInput } from "@/components/admin/boolean-input";
import { DateInput } from "@/components/admin/date-input";
import { SaveButton } from "@/components/admin/form";
import { NumberInput } from "@/components/admin/number-input";
import { SelectInput } from "@/components/admin/select-input";
import { TextInput } from "@/components/admin/text-input";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";

import { toMonth } from "../../providers/commons/financeSchedule";
import type { FinanceRecurringExpense } from "../../types";
import { toNullableText } from "../formValues";
import { useFinanceErrorHandler } from "../useFinanceErrorHandler";
import { expenseCategoryChoices } from "../financeOptions";

const RESOURCE = "finance_recurring_expenses";

export interface RecurringExpenseDialogProps {
  open: boolean;
  onClose: () => void;
  template?: FinanceRecurringExpense | null;
}

/** Template of an expense created every month by "Generate the month" */
export const RecurringExpenseDialog = ({
  open,
  onClose,
  template,
}: RecurringExpenseDialogProps) => {
  const translate = useTranslate();
  const notify = useNotify();
  const onError = useFinanceErrorHandler();
  const [create] = useCreate();
  const [update] = useUpdate();
  const [deleteOne, { isPending: isDeleting }] = useDelete();
  const record = useMemo(
    () =>
      template ?? {
        category: "infrastructure",
        due_day: 10,
        active: true,
        start_date: toMonth(new Date()),
      },
    [template],
  );
  const done = (message: string) => () => {
    notify(message);
    onClose();
  };

  const handleSubmit = async (values: FieldValues) => {
    const data: Partial<FinanceRecurringExpense> = {
      description: values.description.trim(),
      category: values.category,
      supplier: toNullableText(values.supplier),
      amount: Number(values.amount),
      due_day: Number(values.due_day),
      active: Boolean(values.active),
      start_date: values.start_date,
      end_date: values.end_date || null,
    };
    if (template) {
      await update(
        RESOURCE,
        { id: template.id, data, previousData: template },
        { onSuccess: done("crm.finance.recurring.updated"), onError },
      );
      return;
    }
    await create(
      RESOURCE,
      { data },
      { onSuccess: done("crm.finance.recurring.created"), onError },
    );
  };

  return (
    <Dialog open={open} onOpenChange={(isOpen) => !isOpen && onClose()}>
      <DialogContent className="max-h-9/10 overflow-y-auto lg:max-w-xl">
        {open && (
          <Form
            record={record}
            onSubmit={handleSubmit}
            className="flex flex-col gap-4"
          >
            <DialogHeader>
              <DialogTitle>
                {translate(
                  template
                    ? "crm.finance.recurring.edit"
                    : "crm.finance.recurring.create",
                )}
              </DialogTitle>
              <DialogDescription>
                {translate("crm.finance.recurring.help")}
              </DialogDescription>
            </DialogHeader>
            <TextInput
              source="description"
              label="crm.finance.fields.description"
              validate={required()}
              helperText={false}
              autoFocus
            />
            <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
              <SelectInput
                source="category"
                label="crm.finance.expenses.fields.category"
                choices={expenseCategoryChoices}
                validate={required()}
                helperText={false}
              />
              <TextInput
                source="supplier"
                label="crm.finance.expenses.fields.supplier"
                helperText={false}
              />
              <NumberInput
                source="amount"
                label="crm.finance.fields.amount"
                min={0}
                step={0.01}
                validate={[required(), minValue(0)]}
                helperText={false}
              />
              <NumberInput
                source="due_day"
                label="crm.finance.recurring.fields.due_day"
                min={1}
                max={28}
                step={1}
                validate={[required(), minValue(1), maxValue(28)]}
                helperText={false}
              />
              <DateInput
                source="start_date"
                label="crm.finance.contracts.fields.start_date"
                validate={required()}
                helperText={false}
              />
              <DateInput
                source="end_date"
                label="crm.finance.contracts.fields.end_date"
                helperText={false}
              />
            </div>
            <BooleanInput
              source="active"
              label="crm.finance.recurring.fields.active"
              helperText={false}
            />
            <DialogFooter className="w-full gap-2 sm:justify-between">
              {template ? (
                <Button
                  type="button"
                  variant="ghost"
                  className="text-destructive"
                  disabled={isDeleting}
                  onClick={() =>
                    deleteOne(
                      RESOURCE,
                      { id: template.id, previousData: template },
                      {
                        onSuccess: done("crm.finance.recurring.deleted"),
                        onError,
                      },
                    )
                  }
                >
                  <Trash2 />
                  {translate("ra.action.delete")}
                </Button>
              ) : (
                <span />
              )}
              <SaveButton />
            </DialogFooter>
          </Form>
        )}
      </DialogContent>
    </Dialog>
  );
};
